import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import { useEntities } from '../hooks/useEntities';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import FilterBar from '../components/ui/FilterBar';
import ActionButton from '../components/ui/ActionButton';

const API = `${API_V1}/ic-transactions`;
const PAGE_SIZE = 50;

const TXN_TYPES = ['SERVICE', 'SALE', 'COST_ALLOCATION', 'LOAN', 'DIVIDEND'] as const;
const STATUSES = ['DRAFT', 'PENDING', 'POSTED', 'ELIMINATED'] as const;

interface ICTxn {
  id: number;
  transaction_number: string;
  transaction_type: string;
  from_entity: string;
  to_entity: string;
  description: string | null;
  transaction_date: string;
  amount: number;
  markup_pct: number;
  markup_amount: number;
  total_amount: number;
  from_currency: string;
  to_currency: string;
  fx_rate: number;
  from_gl_account: string | null;
  to_gl_account: string | null;
  from_je_id: number | null;
  to_je_id: number | null;
  status: string;
  elimination_je_id: number | null;
  reference: string | null;
  version: number;
  created_at: string | null;
}

interface Summary {
  total_transactions: number;
  total_volume: number;
  open_transactions: number;
  posted_transactions: number;
  eliminated_transactions: number;
  entity_pairs: { from_entity: string; to_entity: string; transaction_count: number; volume: number }[];
}

interface BalanceRow {
  from_entity: string;
  to_entity: string;
  amount: number;
  transaction_count: number;
  currency: string;
  status: string;
}

const emptyForm = {
  from_entity: '',
  to_entity: '',
  transaction_type: 'SERVICE',
  description: '',
  transaction_date: '',
  amount: '',
  markup_pct: '0',
  from_currency: 'USD',
  to_currency: 'USD',
  fx_rate: '1',
  from_gl_account: '',
  to_gl_account: '',
  reference: '',
};

const thStyle: React.CSSProperties = { padding: '10px 14px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)', background: 'var(--bg-card)', whiteSpace: 'nowrap' };
const tdStyle: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)' };

export default function ICTransactions() {
  const entities = useEntities();
  const [txns, setTxns] = useState<ICTxn[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showElimination, setShowElimination] = useState(false);
  const [showBalance, setShowBalance] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [tab, setTab] = useState('transactions');

  // Elimination form
  const [elimPeriod, setElimPeriod] = useState('');
  const [elimResult, setElimResult] = useState<{ eliminations: number; total_eliminated: number } | null>(null);
  const [elimRunning, setElimRunning] = useState(false);

  // Balance report
  const [balances, setBalances] = useState<BalanceRow[]>([]);
  const [balancePeriod, setBalancePeriod] = useState('');
  const [totalOutstanding, setTotalOutstanding] = useState(0);

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (statusFilter) p.set('status', statusFilter);
    if (fromFilter) p.set('from_entity', fromFilter);
    if (toFilter) p.set('to_entity', toFilter);
    if (periodFilter) p.set('period', periodFilter);
    try {
      const [listRes, sumRes] = await Promise.all([authFetch(`${API}?${p}`), authFetch(`${API}/summary`)]);
      if (listRes.ok) { const d = await listRes.json(); setTxns(d.transactions || []); setTotal(d.total || 0); }
      if (sumRes.ok) { setSummary(await sumRes.json()); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, fromFilter, toFilter, periodFilter]);

  useEffect(() => { load(); }, [load]);

  const loadBalances = useCallback(async () => {
    try {
      const p = balancePeriod ? `?period=${balancePeriod}` : '';
      const res = await authFetch(`${API}/balance-report${p}`);
      if (res.ok) {
        const d = await res.json();
        setBalances(d.balances || []);
        setTotalOutstanding(d.total_outstanding || 0);
      }
    } catch (e) { console.error(e); }
  }, [balancePeriod]);

  useEffect(() => { if (tab === 'balances') loadBalances(); }, [tab, loadBalances]);

  const handleCreate = async () => {
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount) || 0,
        markup_pct: parseFloat(form.markup_pct) || 0,
        fx_rate: parseFloat(form.fx_rate) || 1,
      };
      const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) { showToast('IC Transaction created', true); setShowCreate(false); setForm({ ...emptyForm }); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error creating transaction', false); }
    } catch { showToast('Failed to create IC transaction', false); }
  };

  const handlePost = async (id: number) => {
    try {
      const res = await authFetch(`${API}/${id}/post`, { method: 'POST' });
      if (res.ok) { showToast('IC Transaction posted (paired JEs created)', true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error posting', false); }
    } catch { showToast('Posting failed', false); }
  };

  const handleElimination = async () => {
    setElimRunning(true);
    setElimResult(null);
    try {
      const res = await authFetch(`${API}/generate-elimination`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period: elimPeriod }),
      });
      if (res.ok) { const d = await res.json(); setElimResult(d); showToast('Elimination entries generated', true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Elimination generation failed', false); }
    setElimRunning(false);
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  // Compute markup preview
  const previewAmount = parseFloat(form.amount) || 0;
  const previewMarkupPct = parseFloat(form.markup_pct) || 0;
  const previewMarkup = previewAmount * previewMarkupPct / 100;
  const previewTotal = previewAmount + previewMarkup;

  const entityOptions = entities.map(e => ({ value: e.entity_code, label: `${e.entity_code} - ${e.entity_name}` }));

  const balanceColumns: Column[] = [
    { key: 'from_entity', label: 'From Entity', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'to_entity', label: 'To Entity', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'amount', label: 'Outstanding Amount', align: 'right', render: (v: number) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{formatAmount(v)}</span> },
    { key: 'transaction_count', label: 'Transactions' },
    { key: 'currency', label: 'Currency' },
    { key: 'status', label: 'Status', render: (v: string) => <StatusBadge status={v} /> },
  ];

  const entityPairColumns: Column[] = [
    { key: 'from_entity', label: 'From Entity', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'to_entity', label: 'To Entity', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'transaction_count', label: 'Transactions' },
    { key: 'volume', label: 'Volume', align: 'right', render: (v: number) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{formatAmount(v)}</span> },
  ];

  return (
    <PageLayout
      title="IC Transactions"
      subtitle="Intercompany transactions, paired JE posting, and consolidation elimination"
      actions={[
        { label: 'Generate Elimination', onClick: () => { setElimResult(null); setShowElimination(true); }, variant: 'secondary' },
        { label: '+ New IC Transaction', onClick: () => { setForm({ ...emptyForm }); setShowCreate(true); } },
      ]}
    >
      {/* Summary KPI Cards */}
      {summary && (
        <KpiGrid
          columns={5}
          items={[
            { label: 'Total Transactions', value: summary.total_transactions },
            { label: 'Total IC Volume', value: formatAmountCompact(summary.total_volume), color: 'var(--accent)' },
            { label: 'Open', value: summary.open_transactions, color: 'var(--warning)' },
            { label: 'Posted', value: summary.posted_transactions, color: 'var(--positive)' },
            { label: 'Eliminated', value: summary.eliminated_transactions, color: 'var(--text-secondary)' },
          ]}
        />
      )}

      {/* Tabs */}
      <TabBar
        tabs={[
          { key: 'transactions', label: 'Transactions' },
          { key: 'balances', label: 'Balance Report' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'transactions' && (
        <>
          {/* Filter Bar */}
          <FilterBar
            filters={[
              { key: 'from', label: 'From Entity', type: 'select', options: entityOptions, value: fromFilter, onChange: v => { setFromFilter(v); setPage(0); }, width: 200 },
              { key: 'to', label: 'To Entity', type: 'select', options: entityOptions, value: toFilter, onChange: v => { setToFilter(v); setPage(0); }, width: 200 },
              { key: 'status', label: 'Status', type: 'select', options: STATUSES.map(s => ({ value: s, label: s })), value: statusFilter, onChange: v => { setStatusFilter(v); setPage(0); }, width: 160 },
            ]}
          />
          {/* Period filter (month input, not supported by FilterBar) */}
          <div style={{ marginTop: -8, marginBottom: 16 }}>
            <input type="month" value={periodFilter} onChange={e => { setPeriodFilter(e.target.value); setPage(0); }} style={{ padding: '7px 12px', fontSize: 13, border: '1px solid var(--border-subtle)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-primary)', maxWidth: 160 }} placeholder="Period" />
          </div>

          {/* Transaction Table — expandable rows, manual table */}
          {loading ? (
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
          ) : txns.length === 0 ? (
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>No IC transactions found.</div>
          ) : (
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['IC #', 'Date', 'Type', 'From Entity', 'To Entity', 'Amount', 'Markup', 'Total', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ ...thStyle, textAlign: ['Amount', 'Markup', 'Total'].includes(h) ? 'right' : 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {txns.map(t => (
                    <>
                      <tr
                        key={t.id}
                        onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                        style={{ cursor: 'pointer', background: expandedId === t.id ? 'var(--bg-hover)' : 'transparent', transition: 'background 0.1s' }}
                        onMouseEnter={e => { if (expandedId !== t.id) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                        onMouseLeave={e => { if (expandedId !== t.id) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--accent)' }}>{t.transaction_number}</td>
                        <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{t.transaction_date}</td>
                        <td style={tdStyle}>{t.transaction_type}</td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{t.from_entity}</td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{t.to_entity}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(t.amount)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: t.markup_amount > 0 ? 'var(--warning)' : 'var(--text-secondary)' }}>{t.markup_pct > 0 ? `${t.markup_pct}% (${formatAmount(t.markup_amount)})` : '-'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(t.total_amount)}</td>
                        <td style={tdStyle}><StatusBadge status={t.status} /></td>
                        <td style={tdStyle} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {(t.status === 'DRAFT' || t.status === 'PENDING') && (
                              <ActionButton label="Post" onClick={(e) => { e.stopPropagation(); handlePost(t.id); }} variant="success" size="sm" />
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedId === t.id && (
                        <tr key={`${t.id}-details`}>
                          <td colSpan={10} style={{ padding: '0 14px 12px 32px', background: 'var(--bg-hover)' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 8, fontSize: 11 }}>
                              <div><span style={{ color: 'var(--text-tertiary)' }}>Description:</span> {t.description || '-'}</div>
                              <div><span style={{ color: 'var(--text-tertiary)' }}>From GL:</span> {t.from_gl_account || '-'}</div>
                              <div><span style={{ color: 'var(--text-tertiary)' }}>To GL:</span> {t.to_gl_account || '-'}</div>
                              <div><span style={{ color: 'var(--text-tertiary)' }}>From Currency:</span> {t.from_currency}</div>
                              <div><span style={{ color: 'var(--text-tertiary)' }}>To Currency:</span> {t.to_currency}</div>
                              <div><span style={{ color: 'var(--text-tertiary)' }}>FX Rate:</span> {t.fx_rate}</div>
                              <div><span style={{ color: 'var(--text-tertiary)' }}>Reference:</span> {t.reference || '-'}</div>
                              <div><span style={{ color: 'var(--text-tertiary)' }}>Seller JE ID:</span> {t.from_je_id || '-'}</div>
                              <div><span style={{ color: 'var(--text-tertiary)' }}>Buyer JE ID:</span> {t.to_je_id || '-'}</div>
                              {t.elimination_je_id && <div><span style={{ color: 'var(--text-tertiary)' }}>Elimination JE:</span> {t.elimination_je_id}</div>}
                              <div><span style={{ color: 'var(--text-tertiary)' }}>Version:</span> {t.version}</div>
                              <div><span style={{ color: 'var(--text-tertiary)' }}>Created:</span> {t.created_at || '-'}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16, alignItems: 'center' }}>
              <ActionButton label="Prev" onClick={() => setPage(p => p - 1)} variant="secondary" size="sm" disabled={page === 0} />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}</span>
              <ActionButton label="Next" onClick={() => setPage(p => p + 1)} variant="secondary" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} />
            </div>
          )}

          {/* Entity Pair Summary */}
          {summary && summary.entity_pairs.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: 'var(--text-primary)' }}>Top Entity Pairs by Volume</h3>
              <DataTable columns={entityPairColumns} rows={summary.entity_pairs} />
            </div>
          )}
        </>
      )}

      {tab === 'balances' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            <input type="month" value={balancePeriod} onChange={e => setBalancePeriod(e.target.value)} style={{ padding: '7px 12px', fontSize: 13, border: '1px solid var(--border-subtle)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-primary)', maxWidth: 180 }} placeholder="Period filter" />
            <ActionButton label="Refresh" onClick={loadBalances} />
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8 }}>Total Outstanding: <strong style={{ color: 'var(--accent)' }}>{formatAmount(totalOutstanding)}</strong></span>
          </div>
          <DataTable
            columns={balanceColumns}
            rows={balances}
            emptyMessage="No outstanding IC balances."
          />
        </div>
      )}

      {/* Create Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New IC Transaction" size="lg">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>From Entity (Seller) *</label>
            <select value={form.from_entity} onChange={e => setForm({ ...form, from_entity: e.target.value })} style={inputStyle}>
              <option value="">Select entity...</option>
              {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} - {e.entity_name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>To Entity (Buyer) *</label>
            <select value={form.to_entity} onChange={e => setForm({ ...form, to_entity: e.target.value })} style={inputStyle}>
              <option value="">Select entity...</option>
              {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} - {e.entity_name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Transaction Type</label>
            <select value={form.transaction_type} onChange={e => setForm({ ...form, transaction_type: e.target.value })} style={inputStyle}>
              {TXN_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Transaction Date</label>
            <input type="date" value={form.transaction_date} onChange={e => setForm({ ...form, transaction_date: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Amount *</label>
            <input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={inputStyle} placeholder="0.00" />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Markup %</label>
            <input type="number" step="0.01" value={form.markup_pct} onChange={e => setForm({ ...form, markup_pct: e.target.value })} style={inputStyle} placeholder="0" />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>From Currency</label>
            <input value={form.from_currency} onChange={e => setForm({ ...form, from_currency: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>To Currency</label>
            <input value={form.to_currency} onChange={e => setForm({ ...form, to_currency: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>FX Rate</label>
            <input type="number" step="0.000001" value={form.fx_rate} onChange={e => setForm({ ...form, fx_rate: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>From GL Account (Revenue)</label>
            <input value={form.from_gl_account} onChange={e => setForm({ ...form, from_gl_account: e.target.value })} style={inputStyle} placeholder="4300-100" />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>To GL Account (Expense)</label>
            <input value={form.to_gl_account} onChange={e => setForm({ ...form, to_gl_account: e.target.value })} style={inputStyle} placeholder="e.g. 6100-100" />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Reference</label>
            <input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} style={inputStyle} placeholder="Contract, PO, etc." />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Description</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={inputStyle} placeholder="Transaction description" />
          </div>
        </div>

        {/* Preview */}
        {previewAmount > 0 && (
          <div style={{ padding: 12, background: 'var(--bg-hover)', borderRadius: 8, marginBottom: 16, fontSize: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Base Amount:</span>
              <strong>{formatAmount(previewAmount)}</strong>
            </div>
            {previewMarkup > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--warning)' }}>
                <span>Markup ({previewMarkupPct}%):</span>
                <strong>{formatAmount(previewMarkup)}</strong>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, paddingTop: 4, borderTop: '1px solid var(--border-subtle)' }}>
              <span style={{ fontWeight: 700 }}>Total Amount:</span>
              <strong style={{ color: 'var(--accent)' }}>{formatAmount(previewTotal)}</strong>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <ActionButton label="Cancel" onClick={() => setShowCreate(false)} variant="secondary" />
          <ActionButton label="Create Transaction" onClick={handleCreate} disabled={!form.from_entity || !form.to_entity || !form.amount} />
        </div>
      </Modal>

      {/* Elimination Modal */}
      <Modal open={showElimination} onClose={() => setShowElimination(false)} title="Generate IC Elimination" size="sm">
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Period (YYYY-MM) *</label>
          <input type="month" value={elimPeriod} onChange={e => setElimPeriod(e.target.value)} style={inputStyle} />
          <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
            Generates elimination JEs for all POSTED IC transactions in this period. This reverses IC Revenue, IC Receivable, IC Payable, and IC Expense for consolidation.
          </p>
        </div>

        {elimResult && (
          <div style={{ padding: 12, background: 'var(--positive-bg, #f0fdf4)', borderRadius: 8, marginBottom: 16, fontSize: 12 }}>
            <div>Transactions eliminated: <strong>{elimResult.eliminations}</strong></div>
            <div>Total amount eliminated: <strong style={{ color: 'var(--accent)' }}>{formatAmount(elimResult.total_eliminated)}</strong></div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <ActionButton label="Close" onClick={() => setShowElimination(false)} variant="secondary" />
          <ActionButton label={elimRunning ? 'Processing...' : 'Generate'} onClick={handleElimination} disabled={!elimPeriod || elimRunning} />
        </div>
      </Modal>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>
          {toast.msg}
        </div>
      )}
    </PageLayout>
  );
}
